import { useEffect, useMemo, useRef, useState, type ReactNode, type SyntheticEvent } from 'react'
import {
	Bug,
	Camera,
	Check,
	CheckCircle,
	ChevronDown,
	ChevronLeft,
	Clock,
	ExternalLink,
	Inbox,
	Info,
	Lightbulb,
	Loader2,
	Mail,
	Maximize2,
	MessageCircle,
	MessageSquare,
	Send,
	Settings,
	Sparkles,
	Star,
	Tag,
	User,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { PlatformIcon } from '@/components/brand-mentions/PlatformIcon'
import { highlightBrand } from '@/components/brand-mentions/mentionUtils'
import { EssentialMetadata } from '@/components/inbox/visitor-context/EssentialMetadata'
import { MetadataField } from '@/components/inbox/visitor-context/MetadataField'
import { MetadataSection } from '@/components/inbox/visitor-context/MetadataSection'
import { Button } from '@/components/ui/button'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
	Dialog,
	DialogContent,
	DialogTitle,
	DialogTrigger,
} from '@/components/ui/dialog'
import { Textarea } from '@/components/ui/textarea'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { cn } from '@/lib/utils'
import { type BrandMentionPlatform } from '@/types/brandMentions'
import { InboxLiveComposer, InboxLiveThread } from '@/components/dashboard/InboxLiveReply'
import './hero-inbox.css'

export type InboxKind =
	| 'bug'
	| 'feature'
	| 'survey'
	| 'review'
	| 'chat'
	| 'contact'
	| 'mention'
const INBOX_KINDS: InboxKind[] = [
	'bug',
	'feature',
	'survey',
	'review',
	'chat',
	'contact',
	'mention',
]
type InboxFilter = InboxKind | 'all'
type InboxSource = 'widget' | BrandMentionPlatform

export type VisitorContext = {
	email?: string
	browser_name?: string
	browser_version?: string
	os_name?: string
	os_version?: string
	device_type?: 'desktop' | 'mobile' | 'tablet'
	screen_width?: number
	screen_height?: number
	current_page_url?: string
	page_title?: string
	timezone?: string
	language?: string
	city?: string
	country_code?: string
	plan?: string
}

type HistoryField = 'status' | 'severity'

type HistoryEvent = {
	field: HistoryField
	oldValue: string
	newValue: string
	by: string
	time: string
}

export type HeroInboxItem = {
	id: string
	kind: InboxKind
	source: InboxSource
	author: string
	title: string
	body: string
	time: string
	draft: string
	meta: string
	status?: string
	severity?: string
	votes?: number
	hasScreenshot?: boolean
	screenshotUrl?: string
	href?: string
	hrefExternal?: boolean
	brandName?: string
	internalNote?: string
	history?: HistoryEvent[]
	context?: VisitorContext
	createdAt: string
	entityId?: string
}

function highlightedCopy(item: HeroInboxItem, text: string) {
	if (item.kind !== 'mention') return text
	return highlightBrand(text, item.brandName ?? 'Feedbask')
}

const TEAM_REPLY_EMAILS = ['john@doe.com', 'sarah@feedbask.com', 'jean@saas.com'] as const

function emailHash(value: string) {
	return Array.from(value).reduce((sum, ch) => sum + ch.charCodeAt(0), 0)
}

function teamReplyEmail(id: string) {
	return TEAM_REPLY_EMAILS[emailHash(id) % TEAM_REPLY_EMAILS.length]
}

function teamReplyAt(iso: string, id: string) {
	return new Date(new Date(iso).getTime() + (18 + (emailHash(id) % 75)) * 60_000).toISOString()
}

function formatInboxDate(iso: string, isFrench: boolean) {
	return new Date(iso).toLocaleDateString(isFrench ? 'fr-FR' : 'en-US', {
		day: 'numeric',
		month: 'short',
		year: 'numeric',
	})
}

function formatInboxDateTime(iso: string, isFrench: boolean) {
	return new Date(iso).toLocaleString(isFrench ? 'fr-FR' : 'en-US', {
		day: 'numeric',
		month: 'short',
		year: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
	})
}

function ThreadMessage({
	from,
	at,
	body,
	isFrench,
	team = false,
}: {
	from: string
	at: string
	body: ReactNode
	isFrench: boolean
	team?: boolean
}) {
	return (
		<div className={cn('fb-inbox-thread-msg', team && 'is-team')}>
			<div>
				<strong>{from}</strong>
				<time dateTime={at}>{formatInboxDateTime(at, isFrench)}</time>
			</div>
			<p>{body}</p>
		</div>
	)
}

function loc(isFrench: boolean, fr: string, en: string) {
	return isFrench ? fr : en
}

function heroInboxItems(isFrench: boolean): HeroInboxItem[] {
	const tx = (fr: string, en: string) => loc(isFrench, fr, en)
	return [
		{
			id: 'mention-x',
			kind: 'mention',
			source: 'x',
			author: 'Alex Martin',
			title: tx('On a testé Feedbask aujourd’hui', 'Tried Feedbask today'),
			body: tx(
				'Le widget est fluide et les retours arrivent enfin au bon endroit.',
				'The widget feels smooth and the responses actually land in one place.'
			),
			time: '3h',
			meta: 'X',
			draft: tx(
				'Merci d’avoir testé Feedbask — tout est pensé pour atterrir au même endroit.',
				'Thanks for trying Feedbask — everything is built to land in one inbox.'
			),
			createdAt: '2026-09-10T15:05:00',
			context: { city: 'Lyon', country_code: 'FR' },
		},
		{
			id: 'review-emma',
			kind: 'review',
			source: 'widget',
			author: 'Emma Cole',
			title: tx(
				'Le meilleur outil de feedback qu’on ait testé',
				'Best feedback tool we have tried'
			),
			body: tx(
				'Inbox claire, widget léger, et l’équipe a enfin une seule source de vérité.',
				'Clear inbox, lightweight widget, and the team finally has one source of truth.'
			),
			time: '2h',
			meta: '5/5',
			draft: tx(
				'Merci Emma — ravi que Feedbask simplifie vraiment le quotidien de l’équipe.',
				'Thanks Emma — glad Feedbask is making the team’s day-to-day easier.'
			),
			createdAt: '2026-09-10T15:28:00',
			context: {
				email: 'emma.cole@example.com',
				browser_name: 'Chrome',
				browser_version: '126.0',
				os_name: 'macOS',
				os_version: '14.5',
				device_type: 'desktop',
				screen_width: 1440,
				screen_height: 900,
				current_page_url: 'https://acme.app/account',
				page_title: tx('Compte', 'Account'),
				timezone: 'Europe/London',
				language: tx('English (UK)', 'English (UK)'),
				city: 'London',
				country_code: 'GB',
				plan: 'Growth',
			},
		},
		{
			id: 'contact-claire',
			kind: 'contact',
			source: 'widget',
			author: 'Claire Dubois',
			title: tx('Facture TVA pour le plan Growth', 'VAT invoice for the Growth plan'),
			body: tx(
				'Bonjour, pouvez-vous nous envoyer une facture avec TVA pour septembre ?',
				'Hi, could you send us a VAT invoice for September?'
			),
			time: '2h',
			meta: tx('Facturation', 'Billing'),
			draft: tx(
				'Bien sûr — je vous envoie la facture TVA dans la journée.',
				'Of course — I will send the VAT invoice today.'
			),
			createdAt: '2026-09-10T15:42:00',
			context: {
				email: 'claire.dubois@example.com',
				browser_name: 'Firefox',
				browser_version: '128.0',
				os_name: 'Windows',
				os_version: '11',
				device_type: 'desktop',
				screen_width: 1920,
				screen_height: 1080,
				current_page_url: 'https://acme.app/billing',
				page_title: tx('Facturation', 'Billing'),
				timezone: 'Europe/Paris',
				language: tx('Français', 'French'),
				city: 'Lille',
				country_code: 'FR',
				plan: 'Growth',
			},
		},
		{
			id: 'mention-reddit',
			kind: 'mention',
			source: 'reddit',
			author: 'u/saasfounders',
			title: tx(
				'Quelle stack pour le feedback in-app ?',
				'What are you using for in-app feedback?'
			),
			body: tx(
				'On cherche quelque chose de simple à installer. Feedbask a l’air propre.',
				'Looking for something easy to install. Feedbask looks clean and centralizes the noise.'
			),
			time: '2h',
			meta: 'Reddit',
			draft: tx(
				'Feedbask centralise bugs, idées, feedback et mentions au même endroit.',
				'Feedbask puts bugs, ideas, feedback and mentions in the same shared inbox.'
			),
			createdAt: '2026-09-10T16:02:00',
			context: { city: 'Paris', country_code: 'FR' },
		},
		{
			id: 'bug-csv',
			kind: 'bug',
			source: 'widget',
			author: 'Jonas Weber',
			title: tx('L’export CSV ne se termine jamais', 'CSV export never finishes'),
			body: tx(
				'Sur la page Insights, l’export reste bloqué à 80 % depuis ce matin.',
				'On the Insights page, the export has been stuck at 80% since this morning.'
			),
			time: '1h',
			meta: tx('Moyenne', 'Medium'),
			status: 'new',
			severity: 'medium',
			internalNote: tx(
				'Timeout probable sur les exports de plus de 10k lignes.',
				'Likely a timeout on exports larger than 10k rows.'
			),
			draft: tx(
				'Merci Jonas — on corrige l’export CSV Insights.',
				'Thanks Jonas — we are fixing the Insights CSV export.'
			),
			createdAt: '2026-09-10T16:18:00',
			context: {
				email: 'jonas.weber@example.com',
				browser_name: 'Chrome',
				browser_version: '126.0',
				os_name: 'Windows',
				os_version: '11',
				device_type: 'desktop',
				screen_width: 1680,
				screen_height: 1050,
				current_page_url: 'https://acme.app/insights',
				page_title: 'Insights',
				timezone: 'Europe/Berlin',
				language: tx('Deutsch', 'German'),
				city: 'Berlin',
				country_code: 'DE',
				plan: 'Starter',
			},
		},
		{
			id: 'survey-csat',
			kind: 'survey',
			source: 'widget',
			author: 'Hugo Bernard',
			title: tx(
				'Un peu perdu au premier onboarding',
				'A bit lost during first onboarding'
			),
			body: tx(
				'Le widget est bien, mais on n’a pas compris où répondre aux tickets.',
				'The widget is nice, but we did not understand where to reply to tickets.'
			),
			time: '1h',
			meta: 'CSAT 2',
			draft: tx(
				'Merci pour le retour — on clarifie l’onboarding pour retrouver l’inbox plus vite.',
				'Thanks for the feedback — we will make it clearer how to get back to the inbox.'
			),
			createdAt: '2026-09-10T16:34:00',
			context: {
				email: 'hugo.bernard@example.com',
				browser_name: 'Safari',
				browser_version: '17.5',
				os_name: 'macOS',
				os_version: '14.5',
				device_type: 'desktop',
				screen_width: 1512,
				screen_height: 982,
				current_page_url: 'https://acme.app/onboarding',
				page_title: 'Onboarding',
				timezone: 'Europe/Paris',
				language: tx('Français', 'French'),
				city: 'Bordeaux',
				country_code: 'FR',
				plan: 'Starter',
			},
		},
		{
			id: 'feature-slack',
			kind: 'feature',
			source: 'widget',
			author: 'Nina Lopez',
			title: tx(
				'Notifications Slack pour les nouveaux bugs',
				'Slack notifications for new bugs'
			),
			body: tx(
				'On voudrait un canal dédié dès qu’un bug critique arrive.',
				'We want a dedicated channel as soon as a critical bug comes in.'
			),
			time: '1h',
			meta: '8 votes',
			status: 'under_consideration',
			votes: 8,
			internalNote: tx(
				'Demandé aussi par 2 comptes Enterprise.',
				'Also requested by 2 Enterprise accounts.'
			),
			draft: tx(
				'Bonne idée — j’ai ajouté votre vote pour les notifs Slack.',
				'Great idea — I added your vote for Slack notifications.'
			),
			createdAt: '2026-09-10T16:48:00',
			context: {
				email: 'nina.lopez@example.com',
				browser_name: 'Chrome',
				browser_version: '126.0',
				os_name: 'macOS',
				os_version: '14.5',
				device_type: 'desktop',
				screen_width: 1728,
				screen_height: 1117,
				current_page_url: 'https://acme.app/settings/integrations',
				page_title: tx('Intégrations', 'Integrations'),
				timezone: 'America/Los_Angeles',
				language: 'English (US)',
				city: 'San Francisco',
				country_code: 'US',
				plan: 'Growth',
			},
		},
		{
			id: 'chat-olivia',
			kind: 'chat',
			source: 'widget',
			author: 'Olivia Park',
			title: tx('Peut-on exporter la roadmap ?', 'Can we export the roadmap?'),
			body: tx(
				'On présente la roadmap au board demain. Un PDF ou un lien public suffirait.',
				'We present the roadmap to the board tomorrow. A PDF or public link would be enough.'
			),
			time: '50m',
			meta: tx('Chat en direct', 'Live chat'),
			draft: tx(
				'Oui — vous pouvez partager un lien public, je vous envoie les étapes.',
				'Yes — you can share a public link. I will send the steps.'
			),
			createdAt: '2026-09-10T17:10:00',
			context: {
				email: 'olivia.park@example.com',
				browser_name: 'Chrome',
				browser_version: '126.0',
				os_name: 'macOS',
				os_version: '14.5',
				device_type: 'desktop',
				screen_width: 1512,
				screen_height: 982,
				current_page_url: 'https://acme.app/roadmap',
				page_title: 'Roadmap',
				timezone: 'America/New_York',
				language: 'English (US)',
				city: 'Boston',
				country_code: 'US',
				plan: 'Growth',
			},
		},
		{
			id: 'survey-nps',
			kind: 'survey',
			source: 'widget',
			author: 'Sarah Johnson',
			title: tx(
				'Le widget s’installe vraiment en 2 minutes',
				'The widget really takes 2 minutes to install'
			),
			body: tx(
				'On a branché Feedbask ce matin. L’équipe voit déjà les retours au même endroit.',
				'We plugged Feedbask in this morning. The team already sees every response in one place.'
			),
			time: '40m',
			meta: 'NPS 9',
			draft: tx(
				'Ravi que l’installation soit fluide. Merci pour la note !',
				'Glad setup was smooth. Thanks for the score!'
			),
			createdAt: '2026-09-10T17:20:00',
			context: {
				email: 'sarah.j@example.com',
				browser_name: 'Chrome',
				browser_version: '126.0',
				os_name: 'macOS',
				os_version: '14.5',
				device_type: 'desktop',
				screen_width: 1920,
				screen_height: 1080,
				current_page_url: 'https://acme.app/pricing',
				page_title: tx('Tarifs', 'Pricing'),
				timezone: 'Europe/Paris',
				language: tx('Français', 'French'),
				city: 'Paris',
				country_code: 'FR',
				plan: 'Growth',
			},
		},
		{
			id: 'review-karim',
			kind: 'review',
			source: 'widget',
			author: 'Karim Haddad',
			title: tx(
				'Très bon, on aimerait plus d’intégrations',
				'Very good, we would like more integrations'
			),
			body: tx(
				'L’inbox partagée est top. Zapier et HubSpot nous feraient gagner encore plus de temps.',
				'The shared inbox is great. Zapier and HubSpot would save us even more time.'
			),
			time: '32m',
			meta: '4/5',
			draft: tx(
				'Merci Karim — les intégrations Zapier et HubSpot sont bien sur la roadmap.',
				'Thanks Karim — Zapier and HubSpot integrations are on the roadmap.'
			),
			createdAt: '2026-09-10T17:28:00',
			context: {
				email: 'karim.haddad@example.com',
				browser_name: 'Edge',
				browser_version: '126.0',
				os_name: 'Windows',
				os_version: '11',
				device_type: 'desktop',
				screen_width: 1920,
				screen_height: 1080,
				current_page_url: 'https://acme.app',
				page_title: 'Acme',
				timezone: 'Africa/Casablanca',
				language: tx('Français', 'French'),
				city: 'Casablanca',
				country_code: 'MA',
				plan: 'Starter',
			},
		},
		{
			id: 'contact-marcus',
			kind: 'contact',
			source: 'widget',
			author: 'Marcus Lee',
			title: tx(
				'Démo Enterprise pour 40 personnes',
				'Enterprise demo for a 40-person team'
			),
			body: tx(
				'On cherche à remplacer Intercom + Canny. Disponible cette semaine ?',
				'Looking to replace Intercom + Canny. Available this week?'
			),
			time: '24m',
			meta: tx('Commercial', 'Sales'),
			draft: tx(
				'Avec plaisir — je vous propose un créneau demain matin.',
				'Happy to help — I can offer a slot tomorrow morning.'
			),
			createdAt: '2026-09-10T17:36:00',
			context: {
				email: 'marcus.lee@example.com',
				browser_name: 'Chrome',
				browser_version: '126.0',
				os_name: 'macOS',
				os_version: '14.5',
				device_type: 'desktop',
				screen_width: 1512,
				screen_height: 982,
				current_page_url: 'https://acme.app/pricing',
				page_title: tx('Tarifs', 'Pricing'),
				timezone: 'America/Chicago',
				language: 'English (US)',
				city: 'Austin',
				country_code: 'US',
				plan: 'Starter',
			},
		},

		{
			id: 'chat-ben',
			kind: 'chat',
			source: 'widget',
			author: 'Ben Walsh',
			title: tx(
				'Les webhooks partent-ils plusieurs fois ?',
				'Do webhooks retry several times?'
			),
			body: tx(
				'On a reçu 3 fois le même event. Est-ce le comportement attendu ?',
				'We received the same event 3 times. Is that expected?'
			),
			time: '8m',
			meta: tx('Chat en direct', 'Live chat'),
			draft: tx(
				'Oui, on retente jusqu’à 3 fois si l’endpoint ne répond pas 2xx.',
				'Yes, we retry up to 3 times if the endpoint does not return 2xx.'
			),
			createdAt: '2026-09-10T17:52:00',
			context: {
				email: 'ben.walsh@example.com',
				browser_name: 'Chrome',
				browser_version: '126.0',
				os_name: 'Linux',
				os_version: 'Ubuntu 24.04',
				device_type: 'desktop',
				screen_width: 1920,
				screen_height: 1080,
				current_page_url: 'https://acme.app/settings/webhooks',
				page_title: 'Webhooks',
				timezone: 'Europe/London',
				language: 'English (UK)',
				city: 'Manchester',
				country_code: 'GB',
				plan: 'Growth',
			},
		},
		{
			id: 'bug-contrast',
			kind: 'bug',
			source: 'widget',
			author: 'Priya Nair',
			title: tx(
				'Contraste insuffisant sur les filtres',
				'Poor contrast on the filters'
			),
			body: tx(
				'En dark mode, les filtres Inbox sont difficiles à lire.',
				'In dark mode, the inbox filters are hard to read.'
			),
			time: '6m',
			meta: tx('Faible', 'Low'),
			status: 'resolved',
			severity: 'low',
			draft: tx(
				'Merci Priya — le contraste des filtres est corrigé en dark mode.',
				'Thanks Priya — filter contrast in dark mode is now fixed.'
			),
			createdAt: '2026-09-10T17:54:00',
			context: {
				email: 'priya.nair@example.com',
				browser_name: 'Chrome',
				browser_version: '126.0',
				os_name: 'macOS',
				os_version: '14.5',
				device_type: 'desktop',
				screen_width: 1512,
				screen_height: 982,
				current_page_url: 'https://acme.app/inbox',
				page_title: 'Inbox',
				timezone: 'Asia/Kolkata',
				language: 'English (US)',
				city: 'Bengaluru',
				country_code: 'IN',
				plan: 'Growth',
			},
		},
		{
			id: 'feature-dark',
			kind: 'feature',
			source: 'widget',
			author: 'Tom Rivera',
			title: tx(
				'Ajouter le dark mode au dashboard',
				'Add dark mode to the dashboard'
			),
			body: tx(
				'Ce serait plus confortable le soir. Plusieurs équipes nous le demandent déjà.',
				'It would be much easier at night. Several teams have already asked for this.'
			),
			time: '2m',
			meta: '12 votes',
			status: 'planned',
			votes: 12,
			internalNote: tx(
				'Demandé par 3 comptes Growth. À prioriser pour T3.',
				'Requested by 3 Growth accounts. Prioritize for Q3.'
			),
			history: [
				{
					field: 'status',
					oldValue: 'new',
					newValue: 'under_consideration',
					by: 'Léa',
					time: tx('3j', '3d'),
				},
				{
					field: 'status',
					oldValue: 'under_consideration',
					newValue: 'planned',
					by: 'Sam',
					time: tx('1j', '1d'),
				},
			],
			draft: tx(
				'Le dark mode est sur notre radar. J’ai ajouté votre vote à la demande.',
				'Dark mode is on our radar. I added your vote to the request.'
			),
			createdAt: '2026-09-10T17:58:00',
			context: {
				email: 'tom.rivera@example.com',
				browser_name: 'Chrome',
				browser_version: '126.0',
				os_name: 'macOS',
				os_version: '14.5',
				device_type: 'desktop',
				screen_width: 1512,
				screen_height: 982,
				current_page_url: 'https://acme.app/dashboard',
				page_title: tx('Tableau de bord', 'Dashboard'),
				timezone: 'America/New_York',
				language: 'English (US)',
				city: 'New York',
				country_code: 'US',
				plan: 'Starter',
			},
		},
		{
			id: 'mention-linkedin',
			kind: 'mention',
			source: 'linkedin',
			author: 'Camille Durand',
			title: tx(
				'Une inbox partagée pour tout le support',
				'A shared inbox for all customer support'
			),
			body: tx(
				'On entend de plus en plus parler de Feedbask. L’équipe produit répond vite.',
				'Hearing more teams talk about Feedbask. Onboarding is clear and the product team replies fast.'
			),
			time: '1m',
			meta: 'LinkedIn',
			draft: tx(
				'Merci Camille — l’inbox partagée est faite pour que toute l’équipe voie la même chose.',
				'Thanks Camille — the shared inbox is how the whole team sees the same picture.'
			),
			createdAt: '2026-09-10T18:00:00',
			context: { city: 'Paris', country_code: 'FR' },
		},
		{
			id: 'bug-ios',
			kind: 'bug',
			source: 'widget',
			author: 'Maya Chen',
			title: tx('Le paiement échoue sur Safari iOS', 'Checkout fails on Safari iOS'),
			body: tx(
				'Impossible de finaliser le checkout sur iPhone. Le bouton reste grisé après le paiement.',
				'Unable to complete payment on iPhone. The button stays disabled after checkout.'
			),
			time: 'now',
			meta: tx('Élevée', 'High'),
			status: 'in_progress',
			severity: 'high',
			hasScreenshot: true,
			screenshotUrl: '/images/landing/checkout-bug-screenshot.jpg',
			internalNote: tx(
				'Reproduit sur iPhone 14, Safari 17. Lié à la feuille Apple Pay.',
				'Reproduced on iPhone 14, Safari 17. Related to the Apple Pay sheet.'
			),
			history: [
				{
					field: 'severity',
					oldValue: 'medium',
					newValue: 'high',
					by: 'Léa',
					time: '4h',
				},
				{
					field: 'status',
					oldValue: 'new',
					newValue: 'in_progress',
					by: 'Sam',
					time: '2h',
				},
			],
			draft: tx(
				'Merci Maya. Nous avons reproduit le problème sur Safari et préparons un correctif. Nous vous tenons au courant.',
				'Thanks, Maya. We reproduced the Safari checkout issue and are working on a fix. We’ll keep you updated.'
			),
			createdAt: '2026-09-10T18:01:00',
			context: {
				email: 'maya.chen@example.com',
				browser_name: 'Safari',
				browser_version: '17.1',
				os_name: 'iOS',
				os_version: '17.1',
				device_type: 'mobile',
				screen_width: 390,
				screen_height: 844,
				current_page_url: 'https://acme.app/checkout',
				page_title: tx('Paiement', 'Checkout'),
				timezone: 'Asia/Singapore',
				language: 'English (US)',
				city: 'Singapore',
				country_code: 'SG',
				plan: 'Growth',
			},
		},
	]
}

const KIND_STYLES: Record<
	InboxKind,
	{ icon: typeof Bug; color: string; bg: string }
> = {
	bug: { icon: Bug, color: 'text-red-700', bg: 'bg-red-50' },
	feature: { icon: Lightbulb, color: 'text-purple-700', bg: 'bg-purple-50' },
	survey: { icon: MessageSquare, color: 'text-amber-700', bg: 'bg-amber-50' },
	review: { icon: Star, color: 'text-emerald-700', bg: 'bg-emerald-50' },
	chat: { icon: MessageCircle, color: 'text-orange-700', bg: 'bg-orange-50' },
	contact: { icon: Mail, color: 'text-teal-700', bg: 'bg-teal-50' },
	mention: { icon: MessageSquare, color: 'text-sky-700', bg: 'bg-sky-50' },
}

function KindBadge({ kind, label }: { kind: InboxKind; label: string }) {
	const { icon: Icon, color, bg } = KIND_STYLES[kind]
	return (
		<span className={cn('inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium', bg, color)}>
			<Icon className="h-3 w-3" />
			{label}
		</span>
	)
}

function statusKey(value: string) {
	return value.toLowerCase().replace(/\s+/g, '_')
}

function isResolvedStatus(value?: string) {
	return Boolean(value && statusKey(value) === 'resolved')
}

function featureStatusValue(value: string) {
	const map: Record<string, string> = {
		new: 'New',
		under_consideration: 'Under Consideration',
		'under consideration': 'Under Consideration',
		planned: 'Planned',
		in_progress: 'In Progress',
		'in progress': 'In Progress',
		launched: 'Launched',
		declined: 'Declined',
	}
	return map[value.toLowerCase()] || value
}

function statusOptions(kind: InboxKind, t: (key: string) => string) {
	if (kind === 'feature') {
		return [
			{ value: 'New', label: t('featureRequestDetailView.statusNew') },
			{ value: 'Under Consideration', label: t('featureRequestDetailView.statusUnderConsideration') },
			{ value: 'Planned', label: t('featureRequestDetailView.statusPlanned') },
			{ value: 'In Progress', label: t('featureRequestDetailView.statusInProgress') },
			{ value: 'Launched', label: t('featureRequestDetailView.statusLaunched') },
			{ value: 'Declined', label: t('featureRequestDetailView.statusDeclined') },
		]
	}
	if (kind === 'bug' || kind === 'contact') {
		return [
			{ value: 'new', label: t('bugReportTable.statusNew') },
			{ value: 'in_progress', label: t('bugReportTable.statusInProgress') },
			{ value: 'resolved', label: t('bugReportTable.statusResolved') },
			{ value: 'closed', label: t('bugReportTable.statusClosed') },
			{ value: 'archived', label: t('bugReportTable.statusArchived') },
		]
	}
	return []
}

function severityOptions(t: (key: string) => string) {
	return [
		{ value: 'critical', label: t('bugReportTable.severityCritical') },
		{ value: 'high', label: t('bugReportTable.severityHigh') },
		{ value: 'medium', label: t('bugReportTable.severityMedium') },
		{ value: 'low', label: t('bugReportTable.severityLow') },
	]
}

function canEditStatus(kind: InboxKind) {
	return kind === 'bug' || kind === 'feature' || kind === 'contact'
}

function canEditSeverity(kind: InboxKind) {
	return kind === 'bug' || kind === 'contact'
}

function canEditInternalNotes(kind: InboxKind) {
	return kind === 'bug' || kind === 'feature' || kind === 'contact'
}

function InternalNotesEditor({
	initialValue,
	onSave,
	isSaving = false,
	readOnly = false,
}: {
	initialValue: string
	onSave?: (value: string) => void
	isSaving?: boolean
	readOnly?: boolean
}) {
	const { t } = useTranslation()
	const [value, setValue] = useState(initialValue)
	const [showSaved, setShowSaved] = useState(false)
	const lastSavedRef = useRef(initialValue)
	const onSaveRef = useRef(onSave)
	const wasSaving = useRef(false)
	onSaveRef.current = onSave

	useEffect(() => {
		if (readOnly || value === lastSavedRef.current) return
		const timer = setTimeout(() => {
			lastSavedRef.current = value
			onSaveRef.current?.(value)
		}, 1000)
		return () => clearTimeout(timer)
	}, [readOnly, value])

	useEffect(() => {
		if (wasSaving.current && !isSaving) {
			setShowSaved(true)
			const timer = setTimeout(() => setShowSaved(false), 2000)
			wasSaving.current = false
			return () => clearTimeout(timer)
		}
		wasSaving.current = isSaving
	}, [isSaving])

	return (
		<MetadataSection
			title={t('bugReportDetailView.internalNotes')}
			icon={Settings}
			collapsible
			defaultOpen
			iconWrapperClassName="bg-tertiary/80 dark:bg-tertiary/80"
			contentClassName="bg-tertiary/50"
		>
			<Textarea
				value={value}
				onChange={(event) => {
					setValue(event.target.value)
					setShowSaved(false)
				}}
				placeholder={t('bugReportDetailView.internalNotesPlaceholder')}
				className="min-h-[100px] resize-none text-sm bg-background/80"
				disabled={readOnly}
			/>
			<div className="flex items-center justify-between gap-2 px-0.5">
				<p className="text-[10px] italic text-muted-foreground">
					{t('bugReportDetailView.internalNotesHint')}
				</p>
				{isSaving ? (
					<span className="inline-flex shrink-0 items-center gap-1 text-[10px] text-muted-foreground">
						<Loader2 className="h-2.5 w-2.5 animate-spin" />
						{t('bugReportDetailView.saving')}
					</span>
				) : showSaved ? (
					<span className="inline-flex shrink-0 items-center gap-1 text-[10px] font-medium text-emerald-500">
						<CheckCircle className="h-2.5 w-2.5" />
						{t('bugReportDetailView.saved')}
					</span>
				) : null}
			</div>
		</MetadataSection>
	)
}

function statusLabel(kind: InboxKind, value: string, t: (key: string) => string) {
	const key = statusKey(value)
	if (kind === 'feature') {
		const keys: Record<string, string> = {
			new: 'featureRequestDetailView.statusNew',
			under_consideration: 'featureRequestDetailView.statusUnderConsideration',
			planned: 'featureRequestDetailView.statusPlanned',
			in_progress: 'featureRequestDetailView.statusInProgress',
			launched: 'featureRequestDetailView.statusLaunched',
			declined: 'featureRequestDetailView.statusDeclined',
		}
		return t(keys[key] || value)
	}
	const keys: Record<string, string> = {
		new: 'bugReportTable.statusNew',
		in_progress: 'bugReportTable.statusInProgress',
		resolved: 'bugReportTable.statusResolved',
		closed: 'bugReportTable.statusClosed',
		archived: 'bugReportTable.statusArchived',
	}
	return t(keys[key] || value)
}

function severityLabel(value: string, t: (key: string) => string) {
	const keys: Record<string, string> = {
		critical: 'bugReportTable.severityCritical',
		high: 'bugReportTable.severityHigh',
		medium: 'bugReportTable.severityMedium',
		low: 'bugReportTable.severityLow',
	}
	return t(keys[value] || value)
}

function statusTone(kind: InboxKind, value: string) {
	const key = statusKey(value)
	if (kind === 'feature') {
		if (key === 'in_progress') return 'bg-orange-100 text-orange-800 border-orange-200'
		if (key === 'planned') return 'bg-amber-100 text-amber-800 border-amber-200'
		if (key === 'under_consideration') return 'bg-purple-100 text-purple-800 border-purple-200'
		if (key === 'launched') return 'bg-green-100 text-green-800 border-green-200'
		if (key === 'declined') return 'bg-gray-100 text-gray-700 border-gray-200'
		return 'bg-sky-100 text-sky-800 border-sky-200'
	}
	if (key === 'in_progress') return 'bg-yellow-100 text-yellow-800 border-yellow-200'
	if (key === 'resolved' || key === 'closed') return 'bg-green-100 text-green-800 border-green-200'
	if (key === 'archived') return 'bg-purple-100 text-purple-800 border-purple-200'
	return 'bg-primary/10 text-primary border-primary/20'
}

function stopChipEvent(event: SyntheticEvent) {
	event.stopPropagation()
}

function StatusChip({
	kind,
	value,
	onChange,
	disabled = false,
}: {
	kind: InboxKind
	value: string
	onChange?: (value: string) => void
	disabled?: boolean
}) {
	const { t } = useTranslation()
	const options = statusOptions(kind, t)
	const key = statusKey(value)
	const selectValue = kind === 'feature' ? featureStatusValue(value) : key === 'open' ? 'new' : key
	const tone = statusTone(kind, selectValue)

	if (!onChange || options.length === 0) {
		return (
			<span className={cn('inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium', tone)}>
				{statusLabel(kind, value, t)}
			</span>
		)
	}

	return (
		<Select value={selectValue} onValueChange={onChange} disabled={disabled}>
			<SelectTrigger
				className={cn(
					'fb-inbox-chip h-6 min-h-6 w-auto gap-1 rounded-full px-2 py-0 text-[11px] font-medium shadow-none focus:ring-1 focus:ring-offset-0',
					tone
				)}
				onClick={stopChipEvent}
				onPointerDown={stopChipEvent}
				onKeyDown={stopChipEvent}
			>
				<SelectValue />
			</SelectTrigger>
			<SelectContent onClick={stopChipEvent} onPointerDown={stopChipEvent}>
				{options.map((option) => (
					<SelectItem key={option.value} value={option.value}>
						{option.label}
					</SelectItem>
				))}
			</SelectContent>
		</Select>
	)
}

function SeverityChip({
	value,
	onChange,
	disabled = false,
}: {
	value: string
	onChange?: (value: string) => void
	disabled?: boolean
}) {
	const { t } = useTranslation()
	const selectValue = value.toLowerCase()
	const tone =
		selectValue === 'critical'
			? 'bg-red-100 text-red-800 border-red-200'
			: selectValue === 'high'
				? 'bg-orange-100 text-orange-800 border-orange-200'
				: selectValue === 'medium'
					? 'bg-amber-100 text-amber-800 border-amber-200'
					: 'bg-lime-100 text-lime-800 border-lime-200'

	if (!onChange) {
		return (
			<span className={cn('inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium', tone)}>
				{severityLabel(value, t)}
			</span>
		)
	}

	return (
		<Select value={selectValue} onValueChange={onChange} disabled={disabled}>
			<SelectTrigger
				className={cn(
					'fb-inbox-chip h-6 min-h-6 w-auto gap-1 rounded-full px-2 py-0 text-[11px] font-medium shadow-none focus:ring-1 focus:ring-offset-0',
					tone
				)}
				onClick={stopChipEvent}
				onPointerDown={stopChipEvent}
				onKeyDown={stopChipEvent}
			>
				<SelectValue />
			</SelectTrigger>
			<SelectContent onClick={stopChipEvent} onPointerDown={stopChipEvent}>
				{severityOptions(t).map((option) => (
					<SelectItem key={option.value} value={option.value}>
						{option.label}
					</SelectItem>
				))}
			</SelectContent>
		</Select>
	)
}

function ScreenshotLightbox({
	src,
	alt,
	children,
}: {
	src: string
	alt: string
	children: ReactNode
}) {
	return (
		<Dialog>
			<DialogTrigger asChild>{children}</DialogTrigger>
			<DialogContent className="max-w-[90vw] max-h-[90vh] w-screen h-screen p-0 bg-black/95 border-none shadow-none flex items-center justify-center [&_button_svg]:text-white [&_button]:absolute [&_button]:top-4 [&_button]:right-4 [&_button]:z-50">
				<DialogTitle className="sr-only">{alt}</DialogTitle>
				<img src={src} alt={alt} className="max-w-full max-h-full object-contain" />
			</DialogContent>
		</Dialog>
	)
}

function CheckoutScreenshotMock() {
	const { t } = useTranslation()
	return (
		<div className="overflow-hidden rounded-lg border border-border/70 bg-slate-50 shadow-sm">
			<div className="flex items-center gap-1.5 border-b border-border/60 bg-slate-800 px-2.5 py-1.5">
				<span className="h-1.5 w-1.5 rounded-full bg-red-400" />
				<span className="h-1.5 w-1.5 rounded-full bg-amber-400" />
				<span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
				<span className="ml-1 truncate text-[11px] text-slate-300">acme.app/checkout</span>
			</div>
			<div className="space-y-2 p-3">
				<div className="h-2 w-2/3 rounded bg-slate-200" />
				<div className="flex items-center justify-between rounded-md bg-white px-3 py-2 text-xs">
					<span className="text-muted-foreground">Total</span>
					<span className="font-semibold text-foreground">$49.00</span>
				</div>
				<div className="rounded-md bg-slate-200 px-3 py-2 text-center text-xs font-semibold text-slate-600">
					{t('landing.heroInbox.payNow')}
				</div>
			</div>
		</div>
	)
}

function ItemAvatar({ item }: { item: HeroInboxItem }) {
	if (item.source !== 'widget') {
		return (
			<div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted">
				<PlatformIcon platform={item.source} className="h-4 w-4" colored />
			</div>
		)
	}

	const { icon: Icon, color, bg } = KIND_STYLES[item.kind]
	return (
		<div className={cn('flex h-10 w-10 shrink-0 items-center justify-center rounded-full', bg)}>
			<Icon className={cn('h-4 w-4', color)} />
		</div>
	)
}

function ItemHistoryList({ item }: { item: HeroInboxItem }) {
	const { t } = useTranslation()
	if (!item.history?.length) return null

	return (
		<div className="space-y-2.5">
			{item.history.map((event, index) => (
				<div
					key={`${event.field}-${index}`}
					className="border-b border-border/50 pb-2.5 last:border-b-0 last:pb-0"
				>
					<div className="flex items-center justify-between gap-2">
						<p className="text-xs font-medium text-foreground">
							{event.field === 'status'
								? t('bugReportDetailView.statusChanged')
								: t('bugReportDetailView.severityChanged')}
						</p>
						<span className="text-xs text-muted-foreground">{event.time}</span>
					</div>
					<p className="mt-0.5 text-xs text-muted-foreground">
						{event.field === 'severity'
							? severityLabel(event.oldValue, t)
							: statusLabel(item.kind, event.oldValue, t)}
						<span className="mx-1.5">→</span>
						<span className="font-medium text-foreground">
							{event.field === 'severity'
								? severityLabel(event.newValue, t)
								: statusLabel(item.kind, event.newValue, t)}
						</span>
					</p>
					<p className="mt-0.5 text-xs text-muted-foreground">
						{t('bugReportDetailView.changedBy')} {event.by}
					</p>
				</div>
			))}
		</div>
	)
}

function PreviewSideContent({
	item,
	compact = false,
	isPro = true,
	isRestricted = false,
	live = false,
	onInternalNoteChange,
	isSavingNote = false,
}: {
	item: HeroInboxItem
	compact?: boolean
	isPro?: boolean
	isRestricted?: boolean
	live?: boolean
	onInternalNoteChange?: (item: HeroInboxItem, notes: string) => void
	isSavingNote?: boolean
}) {
	const { t } = useTranslation()
	const context = item.context || {}

	return (
		<div className="space-y-3">
			{(item.author || context.email) && (
				<MetadataSection title={t('essentialMetadata.profile')} icon={User} collapsible>
					{item.author && (
						<MetadataField
							label={t('essentialMetadata.name')}
							value={item.author}
							icon={User}
						/>
					)}
					{context.email && (
						<MetadataField
							label={t('essentialMetadata.email')}
							value={context.email}
							icon={Mail}
						/>
					)}
				</MetadataSection>
			)}
			{!compact && canEditInternalNotes(item.kind) && (onInternalNoteChange || item.internalNote) && (
				onInternalNoteChange || live ? (
					<InternalNotesEditor
						key={item.id}
						initialValue={item.internalNote || ''}
						onSave={onInternalNoteChange ? (notes) => onInternalNoteChange(item, notes) : undefined}
						isSaving={isSavingNote}
						readOnly={!onInternalNoteChange}
					/>
				) : (
					<MetadataSection
						title={t('landing.heroInbox.internalNotes')}
						icon={Settings}
						collapsible
						iconWrapperClassName="bg-tertiary/80 dark:bg-tertiary/80"
						contentClassName="bg-tertiary/50"
					>
						<p className="text-sm leading-relaxed text-foreground">{item.internalNote}</p>
					</MetadataSection>
				)
			)}
			{item.hasScreenshot && (
				<MetadataSection title={t('bugReportTable.screenshot')} icon={Camera} collapsible>
					{item.screenshotUrl ? (
						<ScreenshotLightbox
							src={item.screenshotUrl}
							alt={t('bugReportDetailView.bugReportScreenshot')}
						>
							<button
								type="button"
								className="relative flex w-full items-center justify-center overflow-hidden rounded-lg border border-border/70 bg-muted/40 group"
							>
								<img
									src={item.screenshotUrl}
									alt={t('bugReportDetailView.bugReportScreenshot')}
									className="max-h-52 w-auto max-w-full object-contain transition-transform group-hover:scale-[1.02]"
								/>
								<div className="absolute inset-0 flex items-center justify-center bg-black/0 transition-colors group-hover:bg-black/20">
									<Maximize2 className="h-6 w-6 text-white opacity-0 transition-opacity group-hover:opacity-75" />
								</div>
							</button>
						</ScreenshotLightbox>
					) : (
						<CheckoutScreenshotMock />
					)}
				</MetadataSection>
			)}
			<EssentialMetadata
				metadata={compact ? {
          browser_name: context.browser_name, browser_version: context.browser_version,
          os_name: context.os_name, os_version: context.os_version, device_type: context.device_type,
          current_page_url: context.current_page_url, page_title: context.page_title,
        } : context}
				isPro={isPro}
				isRestricted={isRestricted}
				collapsible
				hideProfile
				visitorName={item.author}
				visitorEmail={context.email}
			/>
			{context.plan && (
				<MetadataSection title={t('landing.heroInbox.account')} icon={Tag} collapsible>
					<MetadataField
						label={t('landing.heroInbox.plan')}
						value={context.plan}
						icon={Tag}
					/>
				</MetadataSection>
			)}
			{!compact && item.history && item.history.length > 0 && (
				<MetadataSection title={t('bugReportDetailView.history')} icon={Clock} collapsible>
					<ItemHistoryList item={item} />
				</MetadataSection>
			)}
		</div>
	)
}

function PreviewContextSidebar({
	item,
	onClose,
	compact = false,
	isPro = true,
	isRestricted = false,
	live = false,
	onInternalNoteChange,
	isSavingNote = false,
}: {
	item: HeroInboxItem
	onClose: () => void
	compact?: boolean
	isPro?: boolean
	isRestricted?: boolean
	live?: boolean
	onInternalNoteChange?: (item: HeroInboxItem, notes: string) => void
	isSavingNote?: boolean
}) {
	const { t } = useTranslation()

	return (
		<aside className="flex h-full w-72 shrink-0 flex-col overflow-hidden border-l bg-background/95 lg:w-80">
			<div className="flex items-center justify-between border-b px-4 py-3">
				<p className="text-sm font-semibold text-foreground">
					{t('visitorContextSidebar.visitorContext')}
				</p>
				<Button
					variant="ghost"
					size="icon"
					className="h-8 w-8 hover:bg-muted"
					onClick={onClose}
					aria-label={t('visitorContextSidebar.closeVisitorInformationSidebar')}
				>
					<ChevronLeft className="h-4 w-4" />
				</Button>
			</div>
			<div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden p-3">
				<PreviewSideContent
					item={item}
					compact={compact}
					isPro={isPro}
					isRestricted={isRestricted}
					live={live}
					onInternalNoteChange={onInternalNoteChange}
					isSavingNote={isSavingNote}
				/>
			</div>
		</aside>
	)
}

export function HeroInboxPreview({
	marketingMode = false,
	live = false,
	items: itemsProp,
	isLoading = false,
	isPro = true,
	onStatusChange,
	onSeverityChange,
	onInternalNoteChange,
	isUpdating = false,
	isSavingNote = false,
	canReply = false,
}: {
	marketingMode?: boolean
	live?: boolean
	items?: HeroInboxItem[]
	isLoading?: boolean
	isPro?: boolean
	onStatusChange?: (item: HeroInboxItem, status: string) => void
	onSeverityChange?: (item: HeroInboxItem, severity: string) => void
	onInternalNoteChange?: (item: HeroInboxItem, notes: string) => void
	isUpdating?: boolean
	isSavingNote?: boolean
	canReply?: boolean
} = {}) {
	const { t, i18n } = useTranslation()
	const isFrench = Boolean(i18n.language?.startsWith('fr'))
	const demoItems = useMemo(() => heroInboxItems(isFrench), [isFrench])
	const items = itemsProp ?? demoItems
	const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)')
	const isDesktop = useMediaQuery('(min-width: 1024px)')
	const generateTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
	const [activeKind, setActiveKind] = useState<InboxFilter>('all')
	const [selectedKinds, setSelectedKinds] = useState<Set<InboxKind>>(
		() => new Set(INBOX_KINDS)
	)
	const [selectedId, setSelectedId] = useState<string | null>(
		marketingMode && !live ? 'bug-ios' : null
	)
	const isRestricted = live && !isPro
	const [mobileDetailOpen, setMobileDetailOpen] = useState(false)
	const [showVisitorInfo, setShowVisitorInfo] = useState(true)
	const [replyText, setReplyText] = useState('')
	const [isGeneratingDraft, setIsGeneratingDraft] = useState(false)
	const [replySent, setReplySent] = useState(false)
	const [keptUserReply, setKeptUserReply] = useState(false)
	const [sentReplies, setSentReplies] = useState<Record<string, { text: string; at: string }[]>>({})

	const kindLabels: Record<InboxKind, string> = {
		bug: t('landing.heroInbox.bugs'),
		feature: t('landing.heroInbox.features'),
		survey: t('landing.heroInbox.surveys'),
		review: t('landing.heroInbox.reviews'),
		chat: t('landing.heroInbox.liveChat'),
		contact: t('landing.heroInbox.contact'),
		mention: t('landing.heroInbox.mentions'),
	}

	const filters: Array<{ id: InboxFilter; label: string }> = [
		{ id: 'all', label: t('landing.heroInbox.all') },
		{ id: 'bug', label: t('landing.heroInbox.bugs') },
		{ id: 'feature', label: t('landing.heroInbox.features') },
		{ id: 'survey', label: t('landing.heroInbox.surveys') },
		{ id: 'review', label: t('landing.heroInbox.reviews') },
		{ id: 'chat', label: t('landing.heroInbox.liveChat') },
		{ id: 'contact', label: t('landing.heroInbox.contact') },
		{ id: 'mention', label: t('landing.heroInbox.mentions') },
	]

	const visibleItems = useMemo(() => {
		const list = live
			? items.filter((item) => selectedKinds.has(item.kind))
			: activeKind === 'all'
				? items
				: items.filter((item) => item.kind === activeKind)
		return live ? list : [...list].reverse()
	}, [activeKind, items, live, selectedKinds])

	const allKindsSelected = selectedKinds.size === INBOX_KINDS.length
	const filterTriggerLabel = allKindsSelected
		? t('landing.heroInbox.all')
		: selectedKinds.size === 0
			? t('landing.heroInbox.none')
			: INBOX_KINDS.filter((kind) => selectedKinds.has(kind))
				.map((kind) => kindLabels[kind])
				.join(', ')

	const selected =
		visibleItems.find((item) => item.id === selectedId) ?? visibleItems[0] ?? null

	useEffect(() => {
		setReplyText('')
		setIsGeneratingDraft(false)
		setReplySent(false)
		setKeptUserReply(false)
		if (generateTimerRef.current) {
			clearTimeout(generateTimerRef.current)
			generateTimerRef.current = null
		}
	}, [selected?.id])

	useEffect(() => () => { if (generateTimerRef.current) clearTimeout(generateTimerRef.current) }, [])

	function applyFilter(kind: InboxFilter) {
		setActiveKind(kind)
		setMobileDetailOpen(false)
	}

	function applyKinds(kinds: Iterable<InboxKind>) {
		setSelectedKinds(new Set(kinds))
		setMobileDetailOpen(false)
	}

	function toggleKind(kind: InboxKind) {
		setSelectedKinds((prev) => {
			const next = new Set(prev)
			if (next.has(kind)) next.delete(kind)
			else next.add(kind)
			return next
		})
		setMobileDetailOpen(false)
	}

	function openItem(id: string) {
		setSelectedId(id)
		setReplyText('')
		setIsGeneratingDraft(false)
		setReplySent(false)
		if (generateTimerRef.current) {
			clearTimeout(generateTimerRef.current)
			generateTimerRef.current = null
		}
		setMobileDetailOpen(true)
		const item = items.find((entry) => entry.id === id)
		if (item?.kind !== 'mention') {
			setShowVisitorInfo(true)
		}
	}

	function generateDraft() {
		if (live || !selected || selected.kind === 'mention' || isGeneratingDraft) return
		setIsGeneratingDraft(true)
		setKeptUserReply(false)
		if (generateTimerRef.current) {
			clearTimeout(generateTimerRef.current)
		}
		const draft = selected.draft
		const hasUserReply = replyText.trim().length > 0
		generateTimerRef.current = setTimeout(() => {
			if (hasUserReply) {
				setKeptUserReply(true)
			} else {
				setReplyText(draft)
			}
			setIsGeneratingDraft(false)
			generateTimerRef.current = null
		}, reducedMotion ? 0 : 700)
	}

	function sendSampleReply() {
		if (live || !selected || selected.kind === 'mention' || !replyText.trim()) return
		const text = replyText.trim()
		setSentReplies((prev) => ({
			...prev,
			[selected.id]: [...(prev[selected.id] ?? []), { text, at: new Date().toISOString() }],
		}))
		setReplyText('')
		setReplySent(true)
	}

	return (
		<div className={cn("relative w-full", marketingMode && "fb-marketing-inbox", live && "fb-live-inbox")}>
			{!marketingMode && (
				<div className="pointer-events-none absolute -inset-10 bg-gradient-to-br from-primary/25 via-transparent to-amber-200/40 blur-3xl" />
			)}
			<div
				className={cn(
					'fb-inbox-frame relative overflow-hidden rounded-2xl border border-gray-200 bg-white',
					marketingMode
						? 'shadow-[0_28px_90px_rgba(47,32,91,0.14)]'
						: 'shadow-2xl'
				)}
			>
				<div className="fb-inbox-layout flex h-[28rem] sm:h-[34rem] lg:h-[40rem]">
					<aside data-inbox-pane="list"
						className={cn(
							'fb-inbox-list flex h-full w-full flex-col border-r border-border/60 bg-background md:w-[320px] md:min-w-[320px] md:max-w-[320px] lg:w-[360px] lg:min-w-[360px] lg:max-w-[360px]',
							mobileDetailOpen && 'hidden md:flex'
						)}
					>
						<div className="border-b border-border/60 bg-card/30 px-3 py-3 sm:px-4">
							{!live && (
							<div className="mb-3 flex items-center justify-between gap-2">
								<div className="flex min-w-0 items-center gap-2">
									<Inbox className="h-5 w-5 shrink-0 text-primary" />
									<p className="truncate text-base font-semibold tracking-tight sm:text-lg">
										{t('landing.heroInbox.title')}
									</p>
									<span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1.5 text-[11px] font-semibold text-primary-foreground">
										{visibleItems.length}
									</span>
								</div>
							</div>
							)}
									{live ? (
										<DropdownMenu>
											<DropdownMenuTrigger asChild>
												<button
													type="button"
													aria-label={t('landing.heroInbox.filterTypes')}
													className="flex h-8 w-full items-center justify-between gap-2 rounded-md border border-input bg-background px-2.5 text-left text-xs font-medium text-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
												>
													<span className="min-w-0 truncate">{filterTriggerLabel}</span>
													<ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
												</button>
											</DropdownMenuTrigger>
											<DropdownMenuContent
												align="start"
												className="w-[var(--radix-dropdown-menu-trigger-width)] min-w-56"
											>
												<div className="flex gap-1 p-1">
													<button
														type="button"
														onClick={() => applyKinds(INBOX_KINDS)}
														className={cn(
															'h-7 flex-1 rounded-md text-xs font-medium transition-colors',
															allKindsSelected
																? 'bg-primary text-primary-foreground'
																: 'bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground'
														)}
													>
														{t('landing.heroInbox.all')}
													</button>
													<button
														type="button"
														onClick={() => applyKinds([])}
														className={cn(
															'h-7 flex-1 rounded-md text-xs font-medium transition-colors',
															selectedKinds.size === 0
																? 'bg-primary text-primary-foreground'
																: 'bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground'
														)}
													>
														{t('landing.heroInbox.none')}
													</button>
												</div>
												<DropdownMenuSeparator />
												{INBOX_KINDS.map((kind) => {
													const isSelected = selectedKinds.has(kind)
													return (
														<DropdownMenuItem
															key={kind}
															onSelect={(event) => {
																event.preventDefault()
																toggleKind(kind)
															}}
															className="gap-2 [&_svg]:size-3"
														>
															<span
																className={cn(
																	'flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border',
																	isSelected
																		? 'border-primary'
																		: 'border-input'
																)}
															>
																{isSelected && (
																	<Check className="h-3 w-3 text-primary" />
																)}
															</span>
															{kindLabels[kind]}
														</DropdownMenuItem>
													)
												})}
											</DropdownMenuContent>
										</DropdownMenu>
									) : (
									<div className="flex flex-wrap gap-1.5">
										{filters.map((filter) => {
									const isActive = activeKind === filter.id
									return (
										<button
											key={filter.id}
											type="button"
											onClick={() => applyFilter(filter.id)}
											aria-pressed={isActive}
											className={cn(
												'shrink-0 rounded-md px-2 py-1 text-[11px] font-medium transition-colors',
												isActive
													? 'bg-primary text-primary-foreground'
													: 'bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground'
											)}
										>
											{filter.label}
										</button>
									)
										})}
									</div>
									)}
						</div>
						<div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden scrollbar-hide">
							{isLoading && items.length === 0 ? (
								<div className="space-y-2 p-3">
									{Array.from({ length: 6 }).map((_, index) => (
										<div key={index} className="h-16 animate-pulse rounded-lg bg-muted" />
									))}
								</div>
							) : visibleItems.length === 0 ? (
								<div className="flex h-full items-center justify-center p-6 text-center">
									<p className="text-sm text-muted-foreground">
										{t('landing.heroInbox.waiting')}
									</p>
								</div>
							) : (
							visibleItems.map((item) => {
								const isSelected = selected?.id === item.id
								const statusChange = live && canEditStatus(item.kind) && onStatusChange
									? (status: string) => onStatusChange(item, status)
									: undefined
								const severityChange = live && canEditSeverity(item.kind) && onSeverityChange
									? (severity: string) => onSeverityChange(item, severity)
									: undefined
								return (
									<div
										key={item.id}
										role="button"
										tabIndex={0}
										onClick={() => openItem(item.id)}
										onKeyDown={(event) => {
											if (event.key === 'Enter' || event.key === ' ') {
												event.preventDefault()
												openItem(item.id)
											}
										}}
										className={cn(
											'w-full p-3 text-left transition-all duration-200',
											'bg-background cursor-pointer border-b border-border/50',
											'hover:shadow-md hover:border-primary/40 hover:bg-primary/5',
											'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
											isSelected &&
												'bg-primary/10 border-primary/50 shadow-sm ring-1 ring-primary/30'
										)}
									>
											<div className="flex items-start gap-2.5">
												<div className="flex w-10 shrink-0 flex-col items-center gap-1">
													<ItemAvatar item={item} />
													{item.status && isResolvedStatus(item.status) && (
														<Check
															className="h-3.5 w-3.5 text-green-600"
															strokeWidth={2.5}
															aria-label={statusLabel(item.kind, item.status, t)}
														/>
													)}
												</div>
												<div className="min-w-0 flex-1">
													<div className="mb-1 flex items-center justify-between gap-2">
														<span className="min-w-0 truncate text-sm font-medium text-foreground">
															{item.author}
														</span>
														<span className="shrink-0 text-xs font-medium text-muted-foreground">
															{item.time === 'now'
																? t('landing.heroInbox.now')
																: item.time}
														</span>
													</div>
													<p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
														{highlightedCopy(item, item.title)}
													</p>
													{((item.status && !isResolvedStatus(item.status)) || item.severity || item.hasScreenshot) && (
														<div className="mt-1.5 flex flex-wrap items-center gap-1.5">
															{item.status && !isResolvedStatus(item.status) && (
																<StatusChip
																	kind={item.kind}
																	value={item.status}
																	onChange={statusChange}
																	disabled={isUpdating}
																/>
															)}
															{item.severity && (
																<SeverityChip
																	value={item.severity}
																	onChange={severityChange}
																	disabled={isUpdating}
																/>
															)}
															{item.hasScreenshot && (
																<span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
																	<Camera className="h-3 w-3" />
																</span>
															)}
														</div>
													)}
												</div>
											</div>
										</div>
									)
								})
							)}
						</div>
					</aside>
					<div
						className={cn(
							'min-w-0 flex-1',
							mobileDetailOpen ? 'flex' : 'hidden md:flex'
						)}
					>
						<main data-inbox-pane="message" className="fb-inbox-message flex min-w-0 flex-1 flex-col overflow-hidden bg-muted/20">
							{selected ? (
								<div
									key={selected.id}
									className="flex h-full flex-col overflow-hidden bg-background"
								>
									<header className="flex-shrink-0 border-b bg-background/95 px-3 py-3">
										<div className="flex items-center gap-3">
											<button
												type="button"
												className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted md:hidden"
												onClick={() => setMobileDetailOpen(false)}
												aria-label={t('landing.heroInbox.back')}
											>
												<ChevronLeft className="h-5 w-5" />
											</button>
											{!live && !marketingMode && <ItemAvatar item={selected} />}
											<div className="min-w-0 flex-1">
												<div className="flex flex-wrap items-center gap-2">
													<p className="truncate text-sm font-semibold sm:text-base">
														{selected.author}
													</p>
													{!live && !marketingMode && (
														<KindBadge kind={selected.kind} label={kindLabels[selected.kind]} />
													)}
													{selected.status && (
														<StatusChip
															kind={selected.kind}
															value={selected.status}
															onChange={
																live && canEditStatus(selected.kind) && onStatusChange
																	? (status) => onStatusChange(selected, status)
																	: undefined
															}
															disabled={isUpdating}
														/>
													)}
													{selected.severity && (
														<SeverityChip
															value={selected.severity}
															onChange={
																live && canEditSeverity(selected.kind) && onSeverityChange
																	? (severity) => onSeverityChange(selected, severity)
																	: undefined
															}
															disabled={isUpdating}
														/>
													)}
												</div>
												<div className="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground">
													{selected.votes != null && (
														<>
															<span>
																{t('landing.heroInbox.votes', { count: selected.votes })}
															</span>
															<span>•</span>
														</>
													)}
													{selected.meta &&
														selected.kind !== 'bug' &&
														selected.kind !== 'feature' &&
														selected.kind !== 'mention' && (
														<>
															<span>{selected.meta}</span>
															<span>•</span>
														</>
													)}
													<span>{formatInboxDate(selected.createdAt, isFrench)}</span>
												</div>
											</div>
											{live && selected.kind === 'mention' && selected.href && (
												selected.hrefExternal ? (
													<Button
														asChild
														variant="outline"
														size="sm"
														className="h-8 shrink-0 gap-1.5 px-2.5"
													>
														<a href={selected.href} target="_blank" rel="noopener noreferrer">
															<ExternalLink className="h-3.5 w-3.5" />
															{t('landing.heroInbox.openItem')}
														</a>
													</Button>
												) : (
													<Button
														asChild
														variant="outline"
														size="sm"
														className="h-8 shrink-0 gap-1.5 px-2.5"
													>
														<Link to={selected.href}>
															<ExternalLink className="h-3.5 w-3.5" />
															{t('landing.heroInbox.openItem')}
														</Link>
													</Button>
												)
											)}
											{!showVisitorInfo && (
												<Button
													type="button"
													variant="ghost"
													size="icon"
													className="hidden h-9 w-9 shrink-0 hover:bg-muted/80 lg:inline-flex"
													onClick={() => setShowVisitorInfo(true)}
													aria-label={t('chatWindow.toggleVisitorInformation')}
													aria-expanded={false}
												>
													<Info className="h-4 w-4" />
												</Button>
											)}
										</div>
									</header>
									<div className="min-h-0 flex-1 overflow-y-auto p-4">
										<article className={cn('mx-auto max-w-lg', marketingMode ? 'space-y-3' : 'space-y-4')}>
											{selected.title && selected.title !== selected.body && (
												<p className="text-base font-semibold leading-snug">
													{highlightedCopy(selected, selected.title)}
												</p>
											)}
											{marketingMode ? (
												<>
													{!(live && selected.kind === 'chat') && (
														<ThreadMessage
															from={selected.context?.email || selected.author}
															at={selected.createdAt}
															body={highlightedCopy(selected, selected.body || selected.title)}
															isFrench={isFrench}
														/>
													)}
													{live && (
														<InboxLiveThread item={selected} isFrench={isFrench} />
													)}
													{!live && selected.kind !== 'mention' && (
														<>
															<ThreadMessage
																from={teamReplyEmail(selected.id)}
																at={teamReplyAt(selected.createdAt, selected.id)}
																body={selected.draft}
																isFrench={isFrench}
																team
															/>
															{(sentReplies[selected.id] ?? []).map((reply, index) => (
																<ThreadMessage
																	key={`${reply.at}-${index}`}
																	from="john@doe.com"
																	at={reply.at}
																	body={reply.text}
																	isFrench={isFrench}
																	team
																/>
															))}
														</>
													)}
												</>
											) : (
												<p className="text-sm leading-relaxed text-muted-foreground">
													{highlightedCopy(selected, selected.body)}
												</p>
											)}
										</article>
										{!isDesktop && (
											<div className="mx-auto mt-6 max-w-lg">
												<PreviewSideContent
													item={selected}
													compact={marketingMode && !live}
													isPro={isPro}
													isRestricted={isRestricted}
													live={live}
													onInternalNoteChange={onInternalNoteChange}
													isSavingNote={isSavingNote}
												/>
											</div>
										)}
									</div>
									{live ? (
										<InboxLiveComposer
											key={selected.id}
											item={selected}
											canWrite={canReply}
										/>
									) : selected.kind !== 'mention' ? (
									<footer className="flex-shrink-0 border-t bg-background/95 p-3">
										<div className="space-y-2">
											<Textarea
												value={replyText}
												onChange={(event) => {
													setReplyText(event.target.value)
													setKeptUserReply(false)
												}}
												aria-label={t('landing.heroInbox.replyPlaceholder')}
												placeholder={t('landing.heroInbox.replyPlaceholder')}
												className={cn('resize-none text-sm', marketingMode ? 'min-h-[56px]' : 'min-h-[72px]')}
												rows={marketingMode ? 2 : 3}
												disabled={isGeneratingDraft}
											/>
											<div className="flex items-center justify-between gap-2">
												<Button
													type="button"
													variant="outline"
													size="sm"
													className="h-8 gap-1.5"
													onClick={generateDraft}
													disabled={isGeneratingDraft}
												>
													{isGeneratingDraft ? (
														<Loader2 className="h-3.5 w-3.5 animate-spin" />
													) : (
														<Sparkles className="h-3.5 w-3.5" />
													)}
													{t('landing.heroInbox.generateDraft')}
												</Button>
												<Button
													size="sm"
													className="h-8 gap-1.5 px-3"
													disabled={!replyText.trim()}
													onClick={sendSampleReply}
												>
													<Send className="h-3.5 w-3.5" />
													{t('landing.heroInbox.send')}
												</Button>
											</div>
											{keptUserReply && (
												<p className="text-xs text-muted-foreground" role="status">
													{isFrench
														? 'Votre réponse a été conservée.'
														: 'Kept your reply.'}
												</p>
											)}
											{replySent && !marketingMode && (
												<p className="text-xs font-medium text-emerald-600" role="status">
													{isFrench ? 'Réponse d’exemple envoyée.' : 'Sample reply sent.'}
												</p>
											)}
										</div>
									</footer>
									) : null}
								</div>
							) : (
								<div className="flex h-full flex-col items-center justify-center p-8 text-center">
									<Inbox className="mb-4 h-14 w-14 text-muted-foreground/40" />
									<p className="text-sm text-muted-foreground">
										{live ? t('landing.heroInbox.waiting') : t('landing.heroInbox.selectItem')}
									</p>
								</div>
							)}
						</main>
						{selected && showVisitorInfo && isDesktop && (
							<div data-inbox-pane="context" className="fb-inbox-context flex h-full">
								<PreviewContextSidebar
									compact={marketingMode && !live}
									item={selected}
									onClose={() => setShowVisitorInfo(false)}
									isPro={isPro}
									isRestricted={isRestricted}
									live={live}
									onInternalNoteChange={onInternalNoteChange}
									isSavingNote={isSavingNote}
								/>
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	)
}
